import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getSellerOrders } from "../utils/api";

function money(x) {
  const n = Number(x || 0);
  return n.toFixed(2);
}

export default function SellerOrders({ token, currentUser }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const role = currentUser?.role || "";
  const isSeller = role === "seller" || role === "admin";

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setErr("");
      setLoading(true);

      if (!token || !isSeller) {
        setOrders([]);
        setLoading(false);
        return;
      }

      try {
        const data = await getSellerOrders(token);
        const list = Array.isArray(data) ? data : [];

        if (!cancelled) setOrders(list);
      } catch (e) {
        if (!cancelled) setErr("Failed to load seller orders.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token, isSeller]);

  if (!token) {
    return (
      <div className="container pagePadBottom">
        <div className="card">
          <div style={{ fontWeight: 700 }}>You're not logged in</div>
          <div className="muted small" style={{ marginTop: 6 }}>
            Log in as a seller/admin to view orders.
          </div>
          <div style={{ marginTop: 12 }}>
            <Link className="btnPrimary" to="/login">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!isSeller) {
    return (
      <div className="container pagePadBottom">
        <div className="card">
          <div style={{ fontWeight: 700 }}>Seller access only</div>
          <div className="muted small" style={{ marginTop: 6 }}>
            This page is for sellers/admins.
          </div>
          <div style={{ marginTop: 12 }}>
            <Link className="btnSecondary" to="/">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container pagePadBottom">
      <div className="pageHeader">
        <div className="pageHeaderLeft">
          <div className="h2">Orders</div>
          <div className="muted small" style={{ marginTop: 6 }}>
            Orders containing your listings.
          </div>
        </div>
        <div className="pageHeaderRight">
          <span className="pill">
            <span className="pillStrong">{orders.length}</span> orders
          </span>
        </div>
      </div>

      {err ? (
        <div className="alert alertDanger">
          <div className="alertTitle">Error</div>
          <div className="alertBody">{err}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="emptyBox">
          <div className="emptyTitle">Loading…</div>
          <div className="muted small">Fetching seller orders.</div>
        </div>
      ) : orders.length === 0 ? (
        <div className="emptyBox">
          <div className="emptyTitle">No orders yet</div>
          <div className="muted small">
            When buyers purchase your items, you'll see them here.
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
          {orders.map((o) => {
            const id = o?.id ?? o?.order_id ?? o?.orderId;
            const status = o?.status || "pending";
            const created = o?.created_at ?? o?.createdAt ?? "";
            const buyer = o?.buyer_name ?? o?.buyer ?? o?.username ?? "";
            const total = o?.total ?? o?.total_price ?? 0;

            const items = Array.isArray(o?.items) ? o.items : [];

            return (
              <div key={String(id)} className="card">
                <div className="cardTitleRow">
                  <div className="cardTitle">Order #{id}</div>
                  <span className="badge">{String(status).toUpperCase()}</span>
                </div>

                <div className="muted small" style={{ marginTop: 6 }}>
                  {created ? `Placed: ${created}` : null}
                  {buyer ? ` • Buyer: ${buyer}` : null}
                </div>

                <div className="divider" />

                {items.length ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    {items.map((it, idx) => {
                      const name = it?.name || "Item";
                      const qty = Number(it?.quantity || 1);
                      const price = Number(it?.price || 0);

                      return (
                        <div
                          key={idx}
                          className="row"
                          style={{ justifyContent: "space-between" }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {name}
                            </div>
                            <div className="muted small">Qty: {qty}</div>
                          </div>
                          <div className="priceBlue">${money(price)}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="muted small">
                    No items field returned for this order.
                  </div>
                )}
                <div style={{ marginTop: 12 }}>
                  <Link className="btnSecondary" to={`/orders/${id}`}>
                    View details
                  </Link>
                </div>

                <div className="divider" />

                <div
                  className="row"
                  style={{ justifyContent: "space-between" }}
                >
                  <div style={{ fontWeight: 700 }}>Total</div>
                  <div className="priceBlue">${money(total)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
